import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, MessageSquare, CheckCircle, Clock, RefreshCw, User,
    TrendingUp, TrendingDown, BarChart2, Calendar, Download, Eye,
    ChevronRight, ChevronLeft, ChevronDown, X, Zap, Target, AlertTriangle, CheckSquare, ListFilter, Activity
} from 'lucide-react';
import {
    ResponsiveContainer, Tooltip as ReTooltip,
    AreaChart, Area, XAxis, YAxis, CartesianGrid, BarChart, Bar, Cell
} from 'recharts';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import useGlobalSearch from '../../hooks/useGlobalSearch';
import { useAuth } from '../../context/AuthContext';
import { useSupportDashboard } from '../../context/SupportDashboardContext';

// ─── Brand & Palette Colors ──────────────────────────────────────────────────
const BRAND_DARK = '#00152E';
const BRAND_BLUE = '#1a3884';

const PALETTE = {
    darkBlue: '#00152E',
    skyBlue: '#2563eb',
    darkMaroon: '#7f1d1d',
    deepOrange: '#c2410c',
    gray: '#64748b'
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';
const getSLAHours = (p) => ({ urgent: 4, high: 12, medium: 48, low: 120 }[p] || 48);

const getSLAStatus = (ticket) => {
    if (ticket.status === 'resolved' || ticket.status === 'closed') return 'resolved';
    const elapsed = (Date.now() - new Date(ticket.createdAt).getTime()) / 3600000;
    const lim = getSLAHours(ticket.priority);
    if (elapsed > lim) return 'overdue';
    if (elapsed > lim * 0.8) return 'at-risk';
    return 'on-time';
};

const getRemainingTime = (ticket) => {
    if (ticket.status === 'resolved' || ticket.status === 'closed') return null;
    const rem = new Date(ticket.createdAt).getTime() + getSLAHours(ticket.priority) * 3600000 - Date.now();
    if (rem <= 0) return null;
    const h = Math.floor(rem / 3600000);
    const m = Math.floor((rem % 3600000) / 60000);
    return h > 0 ? h + 'h ' + m + 'm' : m + 'm';
};

const getOverdueBy = (ticket) => {
    const elapsed = (Date.now() - new Date(ticket.createdAt).getTime()) / 3600000;
    const lim = getSLAHours(ticket.priority);
    const over = elapsed - lim;
    if (over <= 0) return '';
    const h = Math.floor(over);
    const m = Math.floor((over % 1) * 60);
    return h > 0 ? h + 'h ' + m + 'm overdue' : m + 'm overdue';
};

// ─── Inline Sub-components ────────────────────────────────────────────────────
const SLABadge = ({ ticket }) => {
    const sla = getSLAStatus(ticket);
    const rem = getRemainingTime(ticket);
    const cfgMap = {
        'on-time': { cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: rem ? 'On Time ' + rem : 'On Time' },
        'at-risk': { cls: 'bg-amber-50 text-amber-700 border-amber-200', label: rem ? 'At Risk ' + rem : 'At Risk' },
        overdue: { cls: 'bg-rose-50 text-rose-700 border-rose-200', label: '⏰ Overdue' },
        resolved: { cls: 'bg-slate-50 text-slate-500 border-slate-200', label: '✓ Done' },
    };
    const { cls, label } = cfgMap[sla] || cfgMap.resolved;
    return (
        <span className={`px-2 py-0.5 text-[10px] font-bold rounded border whitespace-nowrap ${cls}`}>
            {label}
        </span>
    );
};

const StatusBadge = ({ status }) => {
    const s = {
        open: 'bg-blue-100 text-blue-800 border-blue-200',
        'in-progress': 'bg-amber-100 text-amber-800 border-amber-200',
        resolved: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        closed: 'bg-slate-100 text-slate-600 border-slate-200',
    };
    return (
        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase border shadow-sm ${s[status] || s.open}`}>
            {(status || '').replace('-', ' ')}
        </span>
    );
};

const PriorityBadge = ({ priority }) => {
    const s = {
        low: 'text-slate-500 bg-slate-50 border-slate-200',
        medium: 'text-blue-700 bg-blue-50 border-blue-200',
        high: 'text-orange-600 bg-orange-50 border-orange-200',
        urgent: 'text-rose-600 bg-rose-50 border-rose-200',
    };
    return (
        <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black uppercase border ${s[priority] || s.medium}`}>
            {priority}
        </span>
    );
};

const CategoryBadge = ({ category }) => {
    const m = {
        technical: 'bg-blue-100 text-blue-800',
        account: 'bg-emerald-100 text-emerald-800',
        course: 'bg-amber-100 text-amber-800',
        assessment: 'bg-violet-100 text-violet-800',
        'career direction': 'bg-rose-100 text-rose-800',
        'placement issue': 'bg-sky-100 text-sky-800',
        'certificates & badges issue': 'bg-fuchsia-100 text-fuchsia-800',
        other: 'bg-gray-100 text-gray-700',
    };
    const cls = m[(category || '').toLowerCase()] || m.other;
    return (
        <span className={`px-2 py-0.5 text-[10px] font-bold rounded capitalize ${cls}`}>
            {category || 'Other'}
        </span>
    );
};

const Sparkline = ({ data, color }) => {
    if (!data || data.length < 2) return null;
    const max = Math.max(...data, 1);
    const w = 80;
    const h = 28;
    const pts = data.map((v, i) => (i / (data.length - 1)) * w + ',' + (h - (v / max) * h)).join(' ');
    return (
        <svg width={w} height={h} className="overflow-visible opacity-70">
            <polyline
                fill="none"
                stroke={color || BRAND_BLUE}
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                points={pts}
            />
        </svg>
    );
};

// ─── Main Dashboard Component ─────────────────────────────────────────────────
const TicketList = () => {
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const supportDashboard = useSupportDashboard();

    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    const [filter, setFilter] = useState({ status: '', priority: '', category: '', dateFrom: '', dateTo: '' });
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', dir: 'desc' });
    const { searchQuery, setSearchQuery, debouncedSearchQuery: debouncedSearch, searchInputRef } =
        useGlobalSearch('', 400, () => setCurrentPage(1));

    const [totalInquiriesPeriod, setTotalInquiriesPeriod] = useState('monthly');

    // Ticket History drawer
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [expandedSidebarMonths, setExpandedSidebarMonths] = useState({});
    const [expandedSidebarCats, setExpandedSidebarCats] = useState({});
    const [historyFilter, setHistoryFilter] = useState({ month: null, category: null });

    const [trendView, setTrendView] = useState('daily');

    // Single active panel among: 'overdue' | 'highTickets' | 'trend' | null
    const [activePanel, setActivePanel] = useState(null);
    const togglePanel = (key) => setActivePanel((p) => (p === key ? null : key));

    // ── Fetch ──────────────────────────────────────────────────────────────────
    const fetchTickets = useCallback(async () => {
        try {
            setLoading(true);
            const params = { ...filter, search: debouncedSearch };
            Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
            const { data } = await api.get('/tickets/admin/all', { params });
            if (data.success) setTickets(data.data);
        } catch (error) {
            if (!navigator.onLine) {
                toast.error('No internet connection. Please check your network and try again.');
            } else {
                toast.error('Failed to fetch tickets');
            }
        } finally {
            setLoading(false);
        }
    }, [filter, debouncedSearch]);

    useEffect(() => { fetchTickets(); }, [fetchTickets]);

    // ── Role-based filter ──────────────────────────────────────────────────────
    const allVisible = useMemo(() =>
        tickets.filter((t) => {
            if (currentUser?.role === 'support') {
                const aId = typeof t.assignedTo === 'object' ? t.assignedTo?._id : t.assignedTo;
                return String(aId) === String(currentUser._id);
            }
            return true;
        }),
        [tickets, currentUser]
    );

    // ── Status update ──────────────────────────────────────────────────────────
    const handleStatusUpdate = async (id, newStatus, e) => {
        e?.stopPropagation();
        try {
            const { data } = await api.put('/tickets/' + id + '/status', { status: newStatus });
            if (data.success) {
                toast.success('Ticket marked as ' + newStatus.replace('-', ' '));
                fetchTickets();
            }
        } catch {
            toast.error('Failed to update status');
        }
    };

    // ── Time-filtered inquiries counts ────────────────────────────────────────
    const getTimeFiltered = useCallback(
        (range, src) => {
            const list = src || allVisible;
            const now = new Date();
            return list.filter((t) => {
                const d = new Date(t.createdAt);
                if (range === 'daily') return d.toDateString() === now.toDateString();
                if (range === 'weekly') return Date.now() - d.getTime() <= 7 * 86400000;
                if (range === 'monthly') return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
                return true;
            });
        },
        [allVisible]
    );

    const getPrevPeriod = useCallback(
        (range, src) => {
            const list = src || allVisible;
            const now = new Date();
            return list.filter((t) => {
                const d = new Date(t.createdAt);
                if (range === 'daily') {
                    const y = new Date(now);
                    y.setDate(y.getDate() - 1);
                    return d.toDateString() === y.toDateString();
                }
                if (range === 'weekly') return Date.now() - d.getTime() > 7 * 86400000 && Date.now() - d.getTime() <= 14 * 86400000;
                if (range === 'monthly') {
                    const pm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
                    return d >= pm && d < new Date(now.getFullYear(), now.getMonth(), 1);
                }
                return false;
            });
        },
        [allVisible]
    );

    const currentPeriodTickets = useMemo(() => getTimeFiltered(totalInquiriesPeriod), [getTimeFiltered, totalInquiriesPeriod]);
    const prevPeriodTickets = useMemo(() => getPrevPeriod(totalInquiriesPeriod), [getPrevPeriod, totalInquiriesPeriod]);
    const trendDelta = currentPeriodTickets.length - prevPeriodTickets.length;
    const trendPct = prevPeriodTickets.length > 0 ? Math.round((trendDelta / prevPeriodTickets.length) * 100) : 0;

    const sparkData = useMemo(() =>
        Array.from({ length: 7 }, (_, i) => {
            const d = new Date();
            d.setDate(d.getDate() - (6 - i));
            return allVisible.filter((t) => new Date(t.createdAt).toDateString() === d.toDateString()).length;
        }),
        [allVisible]
    );

    // ── Ticket History Sidebar resolved ticket grouping ───────────────────────
    const resolvedMonthHistory = useMemo(() => {
        const resolvedTickets = allVisible.filter(t => t.status === 'resolved' || t.status === 'closed');
        const map = {};

        resolvedTickets.forEach(t => {
            const d = new Date(t.createdAt);
            const monthKey = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
            const monthLabel = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

            let catLabel = 'Other';
            const cat = (t.category || '').toLowerCase();
            if (cat.includes('technical')) catLabel = 'Technical';
            else if (cat.includes('account')) catLabel = 'Accounts';
            else if (cat.includes('career')) catLabel = 'Career Direction';
            else if (cat.includes('course') && cat.includes('assessment')) catLabel = 'Course';
            else if (cat.includes('course')) catLabel = 'Course';
            else if (cat.includes('assessment')) catLabel = 'Assessment';
            else if (cat.includes('placement')) catLabel = 'Placement';
            else if (cat.includes('certificate') || cat.includes('badge')) catLabel = 'Certificates & Badges';

            if (!map[monthKey]) {
                map[monthKey] = { key: monthKey, label: monthLabel, categories: {} };
            }
            if (!map[monthKey].categories[catLabel]) {
                map[monthKey].categories[catLabel] = { name: catLabel, tickets: [] };
            }
            map[monthKey].categories[catLabel].tickets.push(t);
        });

        return Object.values(map)
            .sort((a, b) => b.key.localeCompare(a.key))
            .map(month => ({
                ...month,
                categories: Object.values(month.categories).sort((a, b) => b.tickets.length - a.tickets.length)
            }));
    }, [allVisible]);

    const toggleSidebarMonth = (monthKey) => {
        setExpandedSidebarMonths(prev => ({ ...prev, [monthKey]: !prev[monthKey] }));
    };

    const toggleSidebarCat = (catKey) => {
        setExpandedSidebarCats(prev => ({ ...prev, [catKey]: !prev[catKey] }));
    };

    // ── High Tickets by Category Bar Chart data ──────────────────────────────
    const barChartData = useMemo(() => {
        const counts = {
            technical: 0,
            accounts: 0,
            course: 0,
            assessment: 0,
            career: 0,
            placement: 0,
            certificates: 0,
            other: 0
        };
        allVisible.forEach(t => {
            const cat = (t.category || '').toLowerCase();
            if (cat.includes('technical')) {
                counts.technical++;
            } else if (cat.includes('account')) {
                counts.accounts++;
            } else if (cat.includes('course') && cat.includes('assessment')) {
                counts.course++;
            } else if (cat.includes('course')) {
                counts.course++;
            } else if (cat.includes('assessment')) {
                counts.assessment++;
            } else if (cat.includes('placement')) {
                counts.placement++;
            } else if (cat.includes('certificate') || cat.includes('badge')) {
                counts.certificates++;
            } else if (cat.includes('career')) {
                counts.career++;
            } else {
                counts.other++;
            }
        });
        return [
            { name: 'Technical', value: counts.technical, fill: '#60a5fa' },
            { name: 'Accounts', value: counts.accounts, fill: PALETTE.darkBlue },
            { name: 'Course', value: counts.course, fill: PALETTE.darkMaroon },
            { name: 'Assessment', value: counts.assessment, fill: PALETTE.deepOrange },
            { name: 'Career Direction', value: counts.career, fill: PALETTE.skyBlue },
            { name: 'Placement', value: counts.placement, fill: '#38bdf8' },
            { name: 'Certificates & Badges', value: counts.certificates, fill: '#fb7185' },
            { name: 'Other', value: counts.other, fill: PALETTE.gray }
        ];
    }, [allVisible]);

    const mostProblematicSector = useMemo(() => {
        if (barChartData.length === 0) return null;
        const sorted = [...barChartData].sort((a, b) => b.value - a.value);
        const top = sorted[0];
        const total = barChartData.reduce((sum, item) => sum + item.value, 0);
        const pct = total > 0 ? Math.round((top.value / total) * 100) : 0;
        return { ...top, pct };
    }, [barChartData]);

    const overdueTickets = useMemo(
        () => allVisible.filter((t) => getSLAStatus(t) === 'overdue'),
        [allVisible]
    );

    // ── Trend chart data ───────────────────────────────────────────────────────
    const trendData = useMemo(() => {
        if (trendView === 'daily') {
            return Array.from({ length: 14 }, (_, i) => {
                const d = new Date();
                d.setDate(d.getDate() - (13 - i));
                const ds = d.toDateString();
                return {
                    label: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
                    created: allVisible.filter((t) => new Date(t.createdAt).toDateString() === ds).length,
                    resolved: allVisible.filter((t) => t.status === 'resolved' && new Date(t.updatedAt || t.createdAt).toDateString() === ds).length,
                };
            });
        }
        if (trendView === 'weekly') {
            return Array.from({ length: 8 }, (_, i) => {
                const start = new Date();
                start.setDate(start.getDate() - (7 - i) * 7);
                const end = new Date(start);
                end.setDate(end.getDate() + 7);
                return {
                    label: 'Wk ' + (i + 1),
                    created: allVisible.filter((t) => { const d = new Date(t.createdAt); return d >= start && d < end; }).length,
                    resolved: allVisible.filter((t) => t.status === 'resolved' && (() => { const d = new Date(t.updatedAt || t.createdAt); return d >= start && d < end; })()).length,
                };
            });
        }
        return Array.from({ length: 6 }, (_, i) => {
            const now = new Date();
            const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
            return {
                label: d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }),
                created: allVisible.filter((t) => { const td = new Date(t.createdAt); return td.getMonth() === d.getMonth() && td.getFullYear() === d.getFullYear(); }).length,
                resolved: allVisible.filter((t) => { const td = new Date(t.createdAt); return t.status === 'resolved' && td.getMonth() === d.getMonth() && td.getFullYear() === d.getFullYear(); }).length,
            };
        });
    }, [allVisible, trendView]);

    // ── Sorted & filtered display tickets ─────────────────────────────────────
    const displayTickets = useMemo(() => {
        let list = [...allVisible];

        if (historyFilter.month) {
            list = list.filter((t) => {
                const d = new Date(t.createdAt);
                const monthKey = d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
                return monthKey === historyFilter.month;
            });
        }
        if (historyFilter.category) {
            list = list.filter((t) => {
                const cat = (t.category || '').toLowerCase();
                const fk = historyFilter.category.toLowerCase();
                if (fk === 'technical') return cat.includes('technical');
                if (fk === 'accounts') return cat.includes('account');
                if (fk === 'career direction') return cat.includes('career');
                if (fk === 'course') return cat.includes('course');
                if (fk === 'assessment') return cat.includes('assessment');
                if (fk === 'placement') return cat.includes('placement');
                if (fk === 'certificates & badges') return cat.includes('certificate') || cat.includes('badge');
                return !cat.includes('technical') && !cat.includes('account') && !cat.includes('career') && !cat.includes('course') && !cat.includes('assessment') && !cat.includes('placement') && !cat.includes('certificate') && !cat.includes('badge');
            });
        }

        if (filter.status) list = list.filter(t => t.status === filter.status);
        if (filter.priority) list = list.filter(t => t.priority === filter.priority);
        if (filter.category) {
            list = list.filter(t => {
                const cat = (t.category || '').toLowerCase();
                const fk = filter.category.toLowerCase();
                if (fk === 'account') return cat.includes('account');
                if (fk === 'career direction') return cat.includes('career');
                if (fk === 'course') return cat.includes('course');
                if (fk === 'assessment') return cat.includes('assessment');
                if (fk === 'technical') return cat.includes('technical');
                if (fk === 'placement issue') return cat.includes('placement');
                if (fk === 'certificates & badges issue') return cat.includes('certificate') || cat.includes('badge');
                return cat.includes(fk);
            });
        }
        if (filter.dateFrom) {
            const fD = new Date(filter.dateFrom); fD.setHours(0, 0, 0, 0);
            list = list.filter(t => new Date(t.createdAt) >= fD);
        }
        if (filter.dateTo) {
            const tD = new Date(filter.dateTo); tD.setHours(23, 59, 59, 999);
            list = list.filter(t => new Date(t.createdAt) <= tD);
        }

        const pOrder = ['low', 'medium', 'high', 'urgent'];
        list.sort((a, b) => {
            const { key, dir } = sortConfig;
            const va = key === 'priority' ? pOrder.indexOf(a.priority) : new Date(a[key]).getTime();
            const vb = key === 'priority' ? pOrder.indexOf(b.priority) : new Date(b[key]).getTime();
            return dir === 'asc' ? va - vb : vb - va;
        });
        return list;
    }, [allVisible, historyFilter, filter, sortConfig]);

    const paginated = useMemo(() =>
        displayTickets.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
        [displayTickets, currentPage]
    );
    const totalPages = Math.ceil(displayTickets.length / itemsPerPage);
    const toggleSort = (key) => setSortConfig((p) => ({ key, dir: p.key === key && p.dir === 'asc' ? 'desc' : 'asc' }));

    const exportCSV = useCallback(() => {
        const hdr = ['Ticket ID', 'Subject', 'Category', 'Requester', 'Priority', 'Status', 'SLA', 'Created', 'Updated'];
        const rows = displayTickets.map((t) => [
            '#' + t.ticketId, t.subject, t.category, t.user?.fullName || 'N/A',
            t.priority, t.status, getSLAStatus(t), fmtDate(t.createdAt), fmtDate(t.updatedAt),
        ]);
        const csv = [hdr, ...rows].map((r) => r.map((v) => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
        const a = Object.assign(document.createElement('a'), {
            href: URL.createObjectURL(new Blob([csv], { type: 'text/csv' })),
            download: 'support_ticket_export_' + Date.now() + '.csv',
        });
        a.click();
        URL.revokeObjectURL(a.href);
        toast.success('Ticket data CSV exported successfully!');
    }, [displayTickets]);

    const handleFilterChange = (e) => { setFilter((p) => ({ ...p, [e.target.name]: e.target.value })); setCurrentPage(1); };
    const clearFilters = () => {
        setFilter({ status: '', priority: '', category: '', dateFrom: '', dateTo: '' });
        setSearchQuery('');
        setHistoryFilter({ month: null, category: null });
    };
    const hasActiveFilters = Object.values(filter).some(Boolean) || searchQuery || historyFilter.month;
    const overdueCount = overdueTickets.length;
    const awaitingCount = allVisible.filter((t) => t.status === 'open' || t.status === 'in-progress').length;
    const resolvedCount = allVisible.filter((t) => t.status === 'resolved').length;
    const resolutionRate = allVisible.length > 0 ? Math.round((resolvedCount / allVisible.length) * 100) : 0;

    // ── Register Overdue / History / Export with the sidebar via context ──────
    useEffect(() => {
        if (!supportDashboard) return;
        supportDashboard.registerActions({
            overdueCount,
            onOverdue: () => setActivePanel('overdue'),
            onHistory: () => setIsSidebarOpen(true),
            onExportCSV: exportCSV,
        });
        return () => supportDashboard.clearActions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overdueCount, exportCSV]);

    const PANEL_BOXES = [
        { key: 'trend', label: 'Ticket Volume Trend', icon: Clock, accent: BRAND_BLUE },
        { key: 'highTickets', label: 'High Tickets by Category', icon: BarChart2, accent: BRAND_BLUE },
        { key: 'overdue', label: 'Overdue', icon: AlertTriangle, accent: '#e11d48', badge: overdueCount },
    ];

    return (
        <div className="min-h-screen bg-[#f4f6fb] animate-fade-in relative overflow-x-hidden">
            <div className="max-w-6xl mx-auto px-4 py-6 space-y-5">
                {/* ── Page Header Block ── */}
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <h1 className="text-xl font-bold text-[#1a3884] dark:text-white mb-1">Tickets Dashboard</h1>
                            <div className="flex items-center gap-2 text-xs text-gray-600">
                                <Activity size={14} className="text-[#1a3884]" />
                                <span className="font-medium">
                                    {new Date().toLocaleDateString('en-US', {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric'
                                    })}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={fetchTickets}
                                disabled={loading}
                                className="flex items-center gap-2 text-sm px-3 py-2 text-white shadow-md border-0 bg-gradient-to-r from-[#1a3884] to-[#284BB3] hover:shadow-lg hover:opacity-90 rounded-lg font-bold transition-all"
                            >
                                <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                                <span>{loading ? 'Refreshing...' : 'Refresh Tickets'}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {/* ── Stat Cards + Toggle Buttons — full width to match search bar ── */}
                <div className="w-full space-y-3">
                    {/* Row 1: Stat Cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="group relative bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] overflow-hidden flex flex-col justify-between">
                            <div className="absolute inset-0 bg-gradient-to-br from-[#00152E] to-[#1a3884] opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
                            <div className="relative z-10 flex-1 flex flex-col justify-between">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1">
                                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Total Inquiries</p>
                                        <div className="relative inline-flex items-center mt-1.5">
                                            <select
                                                value={totalInquiriesPeriod}
                                                onChange={(e) => setTotalInquiriesPeriod(e.target.value)}
                                                className="appearance-none bg-slate-100 border border-slate-200 text-slate-700 font-bold py-0.5 pl-2 pr-6 rounded text-[9px] outline-none cursor-pointer"
                                            >
                                                <option value="daily">Daily</option>
                                                <option value="weekly">Weekly</option>
                                                <option value="monthly">Monthly</option>
                                            </select>
                                            <ChevronDown className="w-2.5 h-2.5 text-slate-500 absolute right-1 pointer-events-none" />
                                        </div>
                                    </div>
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                                        <MessageSquare className="text-white w-5 h-5" />
                                    </div>
                                </div>
                                <div className="flex items-center justify-between gap-3 mt-2">
                                    <div>
                                        <h3 className="text-2xl font-bold text-slate-900 leading-none">{currentPeriodTickets.length}</h3>
                                        <div className="flex items-center gap-1.5 mt-1">
                                            {trendDelta >= 0 ? (
                                                <>
                                                    <TrendingUp className="w-3 h-3 text-rose-500 shrink-0" />
                                                    <span className="text-[10px] font-bold text-rose-500">+{trendDelta} ({trendPct > 0 ? '+' + trendPct : '0'}%)</span>
                                                </>
                                            ) : (
                                                <>
                                                    <TrendingDown className="w-3 h-3 text-emerald-500 shrink-0" />
                                                    <span className="text-[10px] font-bold text-emerald-500">{trendDelta} ({trendPct}%)</span>
                                                </>
                                            )}
                                            <span className="text-[9px] text-slate-400">vs prev</span>
                                        </div>
                                    </div>
                                    <Sparkline data={sparkData} color={BRAND_BLUE} />
                                </div>
                            </div>
                        </div>

                        <div className="group relative bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] overflow-hidden flex flex-col justify-between">
                            <div className="absolute inset-0 bg-gradient-to-br from-[#00152E] to-[#1a3884] opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
                            <div className="relative z-10 flex-1 flex flex-col justify-between">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1">
                                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Awaiting Action</p>
                                        <p className="text-xs text-gray-400 mt-0.5">Open + In Progress</p>
                                    </div>
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                                        <Clock className="text-white w-5 h-5" />
                                    </div>
                                </div>
                                <div className="mt-2">
                                    <h3 className="text-2xl font-bold text-slate-900 leading-none">{awaitingCount}</h3>
                                    <p className="text-[10px] text-amber-500 font-bold mt-1">Open + In Progress</p>
                                    <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
                                        <div className="h-full bg-amber-400 rounded-full transition-all"
                                            style={{ width: (allVisible.length > 0 ? awaitingCount / allVisible.length * 100 : 0) + '%' }} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="group relative bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:shadow-lg transition-all duration-300 hover:scale-[1.02] overflow-hidden flex flex-col justify-between">
                            <div className="absolute inset-0 bg-gradient-to-br from-[#00152E] to-[#1a3884] opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
                            <div className="relative z-10 flex-1 flex flex-col justify-between">
                                <div className="flex items-start justify-between mb-2">
                                    <div className="flex-1">
                                        <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Resolved</p>
                                        <p className="text-xs text-gray-400 mt-0.5">{resolutionRate}% resolution rate</p>
                                    </div>
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00152E] to-[#1a3884] flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                                        <CheckCircle className="text-white w-5 h-5" />
                                    </div>
                                </div>
                                <div className="mt-2">
                                    <h3 className="text-2xl font-bold text-slate-900 leading-none">{resolvedCount}</h3>
                                    <p className="text-[10px] text-emerald-500 font-bold mt-1">{resolutionRate}% resolution rate</p>
                                    <div className="mt-2 h-1 bg-slate-100 rounded-full overflow-hidden">
                                        <div className="h-full bg-emerald-400 rounded-full transition-all"
                                            style={{ width: resolutionRate + '%' }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Row 2: Toggle buttons — same gap-4 as stat cards row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {PANEL_BOXES.map(({ key, label, icon: Icon, accent, badge }) => {
                            const isActive = activePanel === key;
                            return (
                                <button
                                    key={key}
                                    onClick={() => togglePanel(key)}
                                    className={`flex items-center justify-between px-4 h-10 rounded-xl border bg-white shadow-sm transition-all text-left ${isActive ? 'border-[#1a3884]/40 shadow-md ring-1 ring-[#1a3884]/20' : 'border-slate-200 hover:shadow-md hover:border-slate-300'}`}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="p-1 rounded-md shrink-0" style={{ background: accent + '18' }}>
                                            <Icon className="w-3 h-3" style={{ color: accent }} />
                                        </div>
                                        <span className="text-[10px] font-black text-slate-700 uppercase tracking-widest leading-none">{label}</span>
                                        {!!badge && (
                                            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black bg-rose-100 text-rose-600">
                                                {badge}
                                            </span>
                                        )}
                                    </div>
                                    <ChevronDown className={`w-3 h-3 text-slate-400 transition-transform duration-200 shrink-0 ${isActive ? 'rotate-180' : ''}`} />
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ── Active panel content — full width ── */}
                {activePanel === 'trend' && (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden animate-slide-up">
                        <div className="border-b border-slate-100 px-5 py-4 flex items-center justify-between flex-wrap gap-2 bg-slate-50/50">
                            <h2 className="text-xs font-bold text-gray-800 uppercase tracking-wider flex items-center gap-2">
                                <Clock className="w-4 h-4 text-[#1a3884]" />
                                Ticket Volume Trend
                            </h2>
                            <div className="flex gap-1 bg-slate-200/60 rounded-lg p-0.5 animate-fade-in">
                                {[['daily', 'Daily'], ['weekly', 'Weekly'], ['monthly', 'Monthly']].map(([v, l]) => (
                                    <button key={v} onClick={() => setTrendView(v)}
                                        className={'px-2.5 py-1 rounded-md text-[9px] font-black transition-all ' + (trendView === v ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-800')}
                                    >
                                        {l}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="p-5">
                            <div className="h-56">
                                <ResponsiveContainer>
                                    <AreaChart data={trendData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                                        <defs>
                                            <linearGradient id="gC" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor={BRAND_BLUE} stopOpacity={0.3} />
                                                <stop offset="95%" stopColor={BRAND_BLUE} stopOpacity={0} />
                                            </linearGradient>
                                            <linearGradient id="gR" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                                                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                        <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 700 }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fontSize: 10, fill: '#94a3b8', fontWeight: 700 }} axisLine={false} tickLine={false} />
                                        <ReTooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: 12 }} />
                                        <Area type="monotone" dataKey="created" name="Created" stroke={BRAND_BLUE} strokeWidth={2.5} fill="url(#gC)" dot={false} />
                                        <Area type="monotone" dataKey="resolved" name="Resolved" stroke="#10b981" strokeWidth={2.5} fill="url(#gR)" dot={false} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                )}

                {activePanel === 'highTickets' && (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden animate-slide-up">
                        <div className="border-b border-slate-100 px-5 py-4 flex items-center gap-2 bg-slate-50/50">
                            <BarChart2 className="w-4 h-4 text-[#1a3884]" />
                            <h2 className="text-xs font-bold text-gray-800 uppercase tracking-wider">High Tickets by Category</h2>
                        </div>
                        <div className="p-5">
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                <div className="lg:col-span-2">
                                    <div className="w-full h-64">
                                        <ResponsiveContainer>
                                            <BarChart data={barChartData} margin={{ top: 10, right: 10, left: -25, bottom: 5 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#64748b', fontWeight: 700 }} axisLine={false} tickLine={false} />
                                                <YAxis tick={{ fontSize: 10, fill: '#64748b', fontWeight: 700 }} axisLine={false} tickLine={false} />
                                                <ReTooltip contentStyle={{ borderRadius: 12, border: 'none', boxShadow: '0 4px 20px rgba(0,0,0,0.1)', fontSize: 11 }} />
                                                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                                                    {barChartData.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                                    ))}
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="flex flex-wrap gap-x-4 gap-y-2 mt-4 text-[10px] font-bold text-slate-500 justify-center">
                                        {barChartData.map((c) => (
                                            <div key={c.name} className="flex items-center gap-1.5">
                                                <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: c.fill }} />
                                                {c.name} ({c.value})
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {mostProblematicSector && (
                                    <div className="flex items-center">
                                        <div className="w-full p-4 rounded-xl border border-slate-200/80 bg-slate-50/50 flex items-center gap-4">
                                            <div className="p-3 bg-[#7f1d1d]/10 rounded-xl shrink-0">
                                                <AlertTriangle className="w-6 h-6 text-[#7f1d1d]" />
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Most Problematic Sector</p>
                                                <h4 className="text-base font-black text-slate-900 capitalize mt-0.5">
                                                    {mostProblematicSector.name}
                                                </h4>
                                                <p className="text-xs text-slate-500 font-semibold mt-1">
                                                    <span className="text-[#7f1d1d] font-bold">{mostProblematicSector.value} tickets</span>
                                                    <span className="mx-1.5 opacity-40">•</span>
                                                    <span>{mostProblematicSector.pct}% of total tickets</span>
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {activePanel === 'overdue' && (
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden animate-slide-up">
                        <div className="border-b border-slate-100 px-5 py-4 flex items-center justify-between flex-wrap gap-2 bg-slate-50/50">
                            <div className="flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-rose-500 animate-pulse" />
                                <h2 className="text-xs font-bold text-gray-800 uppercase tracking-wider">Overdue Tickets</h2>
                            </div>
                            <span className="text-[10px] font-bold bg-rose-100 text-rose-600 px-2 py-0.5 rounded-full">{overdueTickets.length} total</span>
                        </div>
                        <div className="p-5">
                            {overdueTickets.length === 0 ? (
                                <p className="text-sm text-slate-400 font-semibold text-center py-10">No overdue tickets right now.</p>
                            ) : (
                                <div className="divide-y divide-slate-50">
                                    {overdueTickets.map((t) => (
                                        <div
                                            key={t._id}
                                            onClick={() => navigate('/support/' + t._id)}
                                            className="flex items-center justify-between gap-4 py-3 cursor-pointer hover:bg-rose-50/40 rounded-xl px-2 transition-colors"
                                        >
                                            <div className="min-w-0">
                                                <p className="text-sm font-bold text-slate-900 truncate">{t.subject}</p>
                                                <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                                    <span className="text-[10px] font-mono font-black px-1.5 py-0.5 rounded" style={{ background: `${BRAND_BLUE}15`, color: BRAND_BLUE }}>#{t.ticketId}</span>
                                                    <CategoryBadge category={t.category} />
                                                    <PriorityBadge priority={t.priority} />
                                                </div>
                                            </div>
                                            <span className="text-xs font-black text-rose-600 whitespace-nowrap">{getOverdueBy(t)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                    <div className="p-4 border-b border-slate-100 space-y-3 bg-slate-50/40">
                        <div className="flex flex-col lg:flex-row gap-3">
                            <div className="relative flex-1">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                                <input ref={searchInputRef} type="text" placeholder="Search by ID, user, or subject..." value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    className="w-full pl-11 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium text-slate-700 placeholder-slate-400 focus:outline-none transition-all" />
                            </div>
                            {[
                                { name: 'status', opts: [['', 'All Status'], ['open', 'Open'], ['in-progress', 'In Progress'], ['resolved', 'Resolved'], ['closed', 'Closed']] },
                                { name: 'priority', opts: [['', 'All Priorities'], ['urgent', 'Urgent'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low']] },
                                { name: 'category', opts: [
                                    ['', 'All Categories'],
                                    ['technical', 'Technical Issue'],
                                    ['account', 'Account Issue'],
                                    ['course', 'Course Issue'],
                                    ['assessment', 'Assessment Issue'],
                                    ['career direction', 'Career Direction Issue'],
                                    ['placement issue', 'Placement Issue'],
                                    ['certificates & badges issue', 'Certificates & Badges Issue'],
                                    ['other', 'Others Issue']
                                ] },
                            ].map(({ name, opts }) => (
                                <select key={name} name={name} value={filter[name]} onChange={handleFilterChange}
                                    className="px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-600 cursor-pointer hover:border-slate-300 focus:outline-none transition-all min-w-[130px]">
                                    {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                            ))}
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Date:</span>
                                <input type="date" name="dateFrom" value={filter.dateFrom} onChange={handleFilterChange} className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 focus:outline-none" />
                                <span className="text-slate-400 text-xs">to</span>
                                <input type="date" name="dateTo" value={filter.dateTo} onChange={handleFilterChange} className="px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-600 focus:outline-none" />
                            </div>
                            {(hasActiveFilters || historyFilter.month) && (
                                <button onClick={clearFilters} className="flex items-center gap-1.5 text-xs font-black text-rose-500 hover:text-rose-700 bg-rose-50 hover:bg-rose-100 px-3 py-2 rounded-xl transition-all">
                                    <X className="w-3 h-3" /> Clear Filters
                                </button>
                            )}
                            {historyFilter.month && (
                                <div className="text-xs font-bold text-slate-600 bg-slate-100 px-3 py-1.5 rounded-xl border border-slate-200 flex items-center gap-1.5">
                                    <span>History Focus: <b>{historyFilter.category || 'All Categories'}</b> ({historyFilter.month})</span>
                                    <button onClick={() => setHistoryFilter({ month: null, category: null })} className="text-rose-500 hover:text-rose-700 font-bold">×</button>
                                </div>
                            )}
                            <span className="ml-auto text-xs font-bold text-slate-400 shrink-0">{displayTickets.length} tickets</span>
                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-100">
                                    {['Inquiry Details', 'Requester', 'Assignee'].map(h => (
                                        <th key={h} className="px-5 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">{h}</th>
                                    ))}
                                    <th className="px-5 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:text-slate-700 transition-colors select-none" onClick={() => toggleSort('priority')}>
                                        Priority {sortConfig.key === 'priority' ? (sortConfig.dir === 'asc' ? '↑' : '↓') : '↕'}
                                    </th>
                                    <th className="px-5 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">SLA</th>
                                    <th className="px-5 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest cursor-pointer hover:text-slate-700 transition-colors select-none" onClick={() => toggleSort('createdAt')}>
                                        Created {sortConfig.key === 'createdAt' ? (sortConfig.dir === 'asc' ? '↑' : '↓') : '↕'}
                                    </th>
                                    <th className="px-5 py-4 text-[10px] font-black text-slate-400 uppercase tracking-widest">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                                {loading ? (
                                    [...Array(6)].map((_, i) => (
                                        <tr key={i}>
                                            {[...Array(7)].map((_, j) => (
                                                <td key={j} className="px-5 py-5">
                                                    <div className="h-4 bg-slate-100 rounded-lg animate-pulse" style={{ width: `${50 + ((i * 3 + j * 7) % 40)}%` }} />
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                ) : displayTickets.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-5 py-20 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="w-20 h-20 bg-slate-50 rounded-2xl flex items-center justify-center">
                                                    <MessageSquare className="w-9 h-9 text-slate-200" />
                                                </div>
                                                <h3 className="text-slate-800 font-black text-xl">No tickets found</h3>
                                                <p className="text-slate-400 text-sm">Try adjusting your filters</p>
                                                {hasActiveFilters && (
                                                    <button onClick={clearFilters} className="text-sm font-bold px-5 py-2 rounded-xl text-white mt-1" style={{ background: BRAND_BLUE }}>
                                                        Clear Filters
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ) : paginated.map(ticket => {
                                    const sla = getSLAStatus(ticket);
                                    const isOverdue = sla === 'overdue';
                                    return (
                                        <tr key={ticket._id} onClick={() => navigate('/support/' + ticket._id)}
                                            className={`group cursor-pointer transition-all duration-200 ${isOverdue ? 'bg-rose-50/40 hover:bg-rose-50/70' : 'hover:bg-slate-50/60'}`}>
                                            <td className="px-5 py-4 max-w-[260px]">
                                                <p className="text-sm font-bold text-slate-900 group-hover:text-[#1a3884] transition-colors line-clamp-1">{ticket.subject}</p>
                                                <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                                                    <span className="text-[10px] font-mono font-black px-1.5 py-0.5 rounded" style={{ background: `${BRAND_BLUE}15`, color: BRAND_BLUE }}>#{ticket.ticketId}</span>
                                                    <CategoryBadge category={ticket.category} />
                                                    <StatusBadge status={ticket.status} />
                                                </div>
                                            </td>
                                            <td className="px-5 py-4">
                                                <div className="flex flex-col gap-0.5">
                                                    <p className="text-sm font-black text-slate-900 leading-tight">{ticket.user?.fullName || 'Anonymous'}</p>
                                                    {ticket.user?.college?.collegeName && (
                                                        <p className="text-[10px] font-semibold text-slate-500 leading-tight break-words max-w-[150px]">{ticket.user.college.collegeName}</p>
                                                    )}
                                                    <p className="text-[10px] text-slate-400 truncate max-w-[130px]">{ticket.user?.email}</p>
                                                </div>
                                            </td>
                                            <td className="px-5 py-4">
                                                {ticket.assignedTo ? (
                                                    <p className="text-xs font-bold text-slate-700 truncate max-w-[120px]">{ticket.assignedTo?.fullName}</p>
                                                ) : <span className="text-xs text-slate-400 font-bold">Unassigned</span>}
                                            </td>
                                            <td className="px-5 py-4"><PriorityBadge priority={ticket.priority} /></td>
                                            <td className="px-5 py-4"><SLABadge ticket={ticket} /></td>
                                            <td className="px-5 py-4">
                                                <p className="text-xs font-bold text-slate-700">{fmtDate(ticket.createdAt)}</p>
                                                <p className="text-[10px] text-slate-400">{fmtTime(ticket.createdAt)}</p>
                                            </td>
                                            <td className="px-5 py-4" onClick={e => e.stopPropagation()}>
                                                <button
                                                    onClick={() => navigate('/support/' + ticket._id)}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm hover:bg-slate-50 text-xs font-bold text-[#1a3884] hover:text-[#00152E] transition-all animate-fade-in"
                                                >
                                                    <Eye className="w-3.5 h-3.5" />
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/30 flex flex-col sm:flex-row items-center justify-between gap-3">
                            <p className="text-xs font-bold text-slate-400">
                                {(currentPage - 1) * itemsPerPage + 1}–{Math.min(currentPage * itemsPerPage, displayTickets.length)} of {displayTickets.length} tickets
                            </p>
                            <div className="flex items-center gap-1.5">
                                <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                                    className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-black text-slate-600 hover:bg-white disabled:opacity-30 transition-all">
                                    Prev
                                </button>
                                {[...Array(Math.min(totalPages, 7))].map((_, i) => {
                                    const pg = totalPages <= 7 ? i + 1 : currentPage <= 4 ? i + 1 : currentPage >= totalPages - 3 ? totalPages - 6 + i : currentPage - 3 + i;
                                    return (
                                        <button key={pg} onClick={() => setCurrentPage(pg)}
                                            className={`w-9 h-9 rounded-xl text-xs font-black transition-all ${currentPage === pg ? 'text-white scale-105 shadow-md' : 'text-slate-400 hover:bg-white hover:text-slate-900'}`}
                                            style={currentPage === pg ? { background: BRAND_BLUE } : {}}>
                                            {pg}
                                        </button>
                                    );
                                })}
                                <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                                    className="px-4 py-2 border border-slate-200 rounded-xl text-xs font-black text-slate-600 hover:bg-white disabled:opacity-30 transition-all">
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Collapsible Sliding Ticket History Sidebar Drawer ─────────── */}
            <div className={`fixed inset-0 z-55 overflow-hidden transition-opacity duration-300 ${isSidebarOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
                <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm transition-opacity duration-300" onClick={() => setIsSidebarOpen(false)} />
                <div className={`absolute top-0 right-0 max-w-md w-full h-full bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out ${isSidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}>
                    <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100" style={{ background: BRAND_DARK }}>
                        <h3 className="text-sm font-black text-white flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-white" />
                            Resolved Tickets History
                        </h3>
                        <button onClick={() => setIsSidebarOpen(false)} className="p-1 rounded-lg hover:bg-white/10 text-white transition-colors">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto divide-y divide-slate-100 p-4 space-y-3">
                        {resolvedMonthHistory.length === 0 ? (
                            <p className="text-xs text-slate-400 text-center py-12 font-bold">No resolved ticket history found.</p>
                        ) : (
                            resolvedMonthHistory.map((m) => {
                                const isMonthExpanded = expandedSidebarMonths[m.key];
                                return (
                                    <div key={m.key} className="border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-slate-50/50">
                                        <div onClick={() => toggleSidebarMonth(m.key)}
                                            className="px-4 py-3 bg-white flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-all select-none">
                                            <span className="text-sm font-black text-slate-800">{m.label}</span>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[10px] font-black px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded-full border border-emerald-200">
                                                    {m.categories.reduce((acc, c) => acc + c.tickets.length, 0)} resolved
                                                </span>
                                                <span className="text-slate-400 text-xs">
                                                    {isMonthExpanded ? '▲' : '▼'}
                                                </span>
                                            </div>
                                        </div>

                                        {isMonthExpanded && (
                                            <div className="p-3 bg-white border-t border-slate-100 space-y-2">
                                                {m.categories.map((c) => {
                                                    const catKey = `${m.key}-${c.name}`;
                                                    const isCatExpanded = expandedSidebarCats[catKey];
                                                    return (
                                                        <div key={c.name} className="border border-slate-100 rounded-lg overflow-hidden">
                                                            <div onClick={() => toggleSidebarCat(catKey)}
                                                                className="px-3 py-2 bg-slate-50/70 flex items-center justify-between cursor-pointer hover:bg-slate-100/80 transition-all select-none">
                                                                <span className="text-xs font-bold text-slate-700 capitalize">{c.name}</span>
                                                                <div className="flex items-center gap-2">
                                                                    <span className="text-[9px] font-extrabold px-1.5 py-0.2 bg-slate-200/80 text-slate-600 rounded-full">
                                                                        {c.tickets.length}
                                                                    </span>
                                                                    <span className="text-slate-400 text-xs">
                                                                        {isCatExpanded ? '▲' : '▼'}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {isCatExpanded && (
                                                                <div className="divide-y divide-slate-50 bg-white">
                                                                    {c.tickets.map((t) => (
                                                                        <div key={t._id}
                                                                            onClick={() => {
                                                                                setHistoryFilter({ month: m.key, category: c.name });
                                                                                setIsSidebarOpen(false);
                                                                                toast.success(`Filtering by resolved ${c.name} issues in ${m.label}`);
                                                                            }}
                                                                            className="px-3 py-2.5 hover:bg-slate-50 cursor-pointer transition-all flex flex-col gap-1 text-left">
                                                                            <div className="flex items-center justify-between">
                                                                                <span className="text-[10px] font-mono font-bold text-slate-400">#{t.itsmTicketNumber || t.ticketId}</span>
                                                                                <span className="text-[9px] font-medium text-slate-400">{fmtDate(t.createdAt)}</span>
                                                                            </div>
                                                                            <span className="text-xs font-bold text-slate-800 line-clamp-1 hover:text-[#1a3884]">{t.subject}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            </div>

        </div>
    );
};

export default TicketList;